import { NgModule } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AgmCoreModule } from '@agm/core';
import { LinkyModule } from 'angular-linky';

import { VarDirective } from './var/var.directive';
import { PhonePipe } from './phone/phone.pipe';
import { ScriptLoaderService } from './script-loader/script-loader.service';
import { SourceComponent } from './source/source.component';
import { ConvertPipe } from './convert/convert.pipe';
import { StateViewComponent } from './state-view/state-view.component';
import { StateViewChildComponent } from './state-view/state-view-child.component';
import { BearingComponent } from './bearing/bearing.component';
import { AddressComponent } from './address/address.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { ErrorComponent } from './error/error.component';
import { GoogleService } from '../google/google.service';
import { AppMaterialModule } from '../app-material.module';
import { ServiceBadgeComponent } from './service-badge/service-badge.component';
import { DataListComponent } from './data-list/data-list.component';
import { DataItemComponent } from './data-item/data-item.component';
import { AutocompleteComponent } from './autocomplete/autocomplete.component';
import { HighlightPipe } from './highlight.pipe';
import { CivilianTimePipe } from './civilian-time/civilian-time.pipe';
import { ActionIconComponent } from './action-icon/action-icon.component';
import { UserService } from './user/user.service';
import { BounceSpinnerComponent } from './bounce-spinner/bounce-spinner.component';
import { DotsSpinnerComponent } from './dots-spinner/dots-spinner.component';
import { TagBulletListComponent } from './tag-bullet-list/tag-bullet-list.component';
import { ReviewsComponent } from './reviews/reviews.component';
import { StarRatingModule } from 'angular-star-rating';
import { RentalCarsComponent } from './rental-cars/rental-cars.component';
import { ReviewOverviewComponent } from './review-overview/review-overview.component';
import { BadgeComponent } from './badge/badge.component';
import { FuelComponent } from './fuel/fuel.component';
import { FuelBadgeComponent } from './fuel-badge/fuel-badge.component';
import { QuickNavComponent } from './quick-nav/quick-nav.component';
import { AppRoutingModule } from '../routing/app-routing.module';
import { WriteReviewComponent } from './write-review/write-review.component';
import { BooleanPipe } from './boolean/boolean.pipe';
import { AutocompleteTriggerComponent } from './autocomplete-trigger/autocomplete-trigger.component';
import { AirportSummaryCardComponent } from './airport-summary-card/airport-summary-card.component';
import { AirportFavoriteIconComponent } from './airport-favorite-icon/airport-favorite-icon.component';
import { DisableControlDirective } from './disable-control/disable-control.directive';
import { DisableGroupDirective } from './disable-group/disable-group.directive';
import { AirportIconsComponent } from './airport-icons/airport-icons.component';
import { HistoryTrackerService } from './history-tracker/history-tracker.service';
import { PlacesInfoWindowComponent } from './places-info-window/places-info-window.component';
import { SummaryFeesComponent } from './summary-fees/summary-fees.component';
import { BusinessFeesComponent } from './business-fees/business-fees.component';
import { AirportHomeIconComponent } from './airport-home-icon/airport-home-icon.component';
import { ActivitySummaryCardComponent } from './activity-summary-card/activity-summary-card.component';
import { OpenXAdunitComponent } from './open-x-adunit/open-x-adunit.component';
import { SkinService } from './skin/skin.service';
import { AppFooterComponent } from './app-footer/app-footer.component';
import { LinkifyPipe } from './linkify/linkify.pipe';
import { AutocompleteOverlayComponent } from './autocomplete-overlay/autocomplete-overlay.component';
import { BusinessManagerService } from './user/business-manager.service';
import { EditTagComponent } from './edit-tag/edit-tag.component';
import { EditTagsComponent } from './edit-tags/edit-tags.component';
import { CountriesService } from './services/countries.service';
import { TagService } from './services/tag.service';
import { EditToggleComponent } from './edit-toggle/edit-toggle.component';
import { EditToggleService } from './services/edit-toggle.service';
import { FrequencyPipe } from './frequency.pipe';
import { TagNamePipe } from './tag-name.pipe';
import { CanDeactivateGuardService } from '../shared/services/can-deactivate-guard.service';
import { DfpModule } from 'ngx-dfp';
import { ThumbsupIconComponent } from './thumbsup-icon/thumbsup-icon.component';
import { ThumbsdownIconComponent } from './thumbsdown-icon/thumbsdown-icon.component';
import { TruncateModule } from '@yellowspot/ng-truncate';
import { TruncatedTextComponent } from './truncated-text/truncated-text.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { EditReviewResponseComponent } from './edit-review-response/edit-review-response.component';
import { NgxGalleryModule } from 'ngx-gallery';
import { ImageUploadComponent } from './image-upload/image-upload.component';
import { ReviewMoveComponent } from './review-move/review-move.component';
import { ReviewReportOffensiveComponent } from './review-report-offensive/review-report-offensive.component';
import { TravelArticlesComponent } from './travel-articles/travel-articles.component';
import { WeatherLegendComponent } from './weather-legend/weather-legend.component';
import { GlobalSettingsService } from './services/global-settings.service';
import { SuggestAChangeComponent } from './suggest-a-change/suggest-a-change.component';
import { EventSummaryCardComponent } from './event-summary-card/event-summary-card.component';
import { NearbyEventsComponent } from './nearby-events/nearby-events.component';
import { EventFavoriteIconComponent } from './event-favorite-icon/event-favorite-icon.component';
import { EventBadgeComponent } from './event-badge/event-badge.component';
import { AddressValidationService } from '../google/address-validation.service';
import { FlightbridgeComponent } from './flightbridge/flightbridge.component';
import { FlightbridgeService } from './services/flightbridge.service';
import { FlightbridgeRightRailComponent } from './flightbridge-right-rail/flightbridge-right-rail.component';
import { ShowDialogComponent } from './show-dialog/show-dialog.component';


@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AgmCoreModule,
    StarRatingModule.forRoot(),
    AppRoutingModule,
    AppMaterialModule,
    LinkyModule,
    DfpModule.forRoot({
      idleLoad: true,
      enableVideoAds: false,
      personalizedAds: false, // Request personalized ads by default
      singleRequestMode: true, // Only applies to initial refresh
      onSameNavigation: 'refresh',
      globalTargeting: {}
    }),
    TruncateModule,
    NgxGalleryModule
  ],
  entryComponents: [
    WriteReviewComponent,
    EditTagsComponent,
    ConfirmationDialogComponent,
    EditReviewResponseComponent,
    ImageUploadComponent,
    ReviewMoveComponent,
    ReviewReportOffensiveComponent,
    WeatherLegendComponent,
    SuggestAChangeComponent,
    ShowDialogComponent
  ],
  declarations: [
    VarDirective,
    PhonePipe,
    SourceComponent,
    ConvertPipe,
    StateViewComponent,
    StateViewChildComponent,
    BearingComponent,
    AddressComponent,
    NotFoundComponent,
    ErrorComponent,
    ServiceBadgeComponent,
    DataListComponent,
    DataItemComponent,
    AutocompleteComponent,
    HighlightPipe,
    CivilianTimePipe,
    ActionIconComponent,
    BounceSpinnerComponent,
    DotsSpinnerComponent,
    TagBulletListComponent,
    ReviewsComponent,
    RentalCarsComponent,
    ReviewOverviewComponent,
    BadgeComponent,
    FuelComponent,
    FuelBadgeComponent,
    QuickNavComponent,
    WriteReviewComponent,
    BooleanPipe,
    AutocompleteTriggerComponent,
    AirportIconsComponent,
    AirportSummaryCardComponent,
    AirportFavoriteIconComponent,
    DisableControlDirective,
    DisableGroupDirective,
    PlacesInfoWindowComponent,
    SummaryFeesComponent,
    BusinessFeesComponent,
    AirportHomeIconComponent,
    ActivitySummaryCardComponent,
    OpenXAdunitComponent,
    AppFooterComponent,
    LinkifyPipe,
    AutocompleteOverlayComponent,
    EditTagComponent,
    EditTagsComponent,
    EditToggleComponent,
    FrequencyPipe,
    TagNamePipe,
    ThumbsupIconComponent,
    ThumbsdownIconComponent,
    TruncatedTextComponent,
    ConfirmationDialogComponent,
    EditReviewResponseComponent,
    ImageUploadComponent,
    ReviewMoveComponent,
    ReviewReportOffensiveComponent,
    TravelArticlesComponent,
    WeatherLegendComponent,
    SuggestAChangeComponent,
    EventSummaryCardComponent,
    NearbyEventsComponent,
    EventFavoriteIconComponent,
    EventBadgeComponent,
    FlightbridgeComponent,
    FlightbridgeRightRailComponent,
    ShowDialogComponent
  ],
    exports: [
        VarDirective,
        PhonePipe,
        SourceComponent,
        ConvertPipe,
        StateViewComponent,
        StateViewChildComponent,
        BearingComponent,
        AddressComponent,
        ServiceBadgeComponent,
        DataListComponent,
        DataItemComponent,
        AutocompleteComponent,
        CivilianTimePipe,
        HighlightPipe,
        ActionIconComponent,
        BounceSpinnerComponent,
        DotsSpinnerComponent,
        TagBulletListComponent,
        ReviewsComponent,
        RentalCarsComponent,
        ReviewOverviewComponent,
        BadgeComponent,
        FuelComponent,
        FuelBadgeComponent,
        QuickNavComponent,
        BooleanPipe,
        AutocompleteTriggerComponent,
        AirportIconsComponent,
        AirportSummaryCardComponent,
        AirportFavoriteIconComponent,
        DisableControlDirective,
        DisableGroupDirective,
        PlacesInfoWindowComponent,
        SummaryFeesComponent,
        BusinessFeesComponent,
        AirportHomeIconComponent,
        ActivitySummaryCardComponent,
        OpenXAdunitComponent,
        AppFooterComponent,
        LinkifyPipe,
        AutocompleteOverlayComponent,
        EditToggleComponent,
        FrequencyPipe,
        TagNamePipe,
        TravelArticlesComponent,
        EventSummaryCardComponent,
        NearbyEventsComponent,
        EventFavoriteIconComponent,
        EventBadgeComponent,
        FlightbridgeComponent,
        FlightbridgeRightRailComponent,
        TruncatedTextComponent
    ],
  providers: [
    CurrencyPipe,
    ScriptLoaderService,
    GoogleService,
    UserService,
    BooleanPipe,
    HistoryTrackerService,
    SkinService,
    LinkifyPipe,
    BusinessManagerService,
    CountriesService,
    TagService,
    EditToggleService,
    CanDeactivateGuardService,
    GlobalSettingsService,
    AddressValidationService,
    FlightbridgeService
  ]
})
export class SharedModule {
}
